import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import {
  ADD_ICON_WITH_HOVER_STYLES,
  ARCHIVED_ANALYSIS_TOOLTIP_TEXT,
} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {
  getAddDataFrameAnalysisViewItem,
  getEnabledDatasetCreationMethods,
  trackSelectedDataFrameCreationMethodEvent,
} from 'analysis-tool/data-frame-creation/DataFrameCreationUtils';
import {
  isLegacyTopLevelAnalysisView,
  isTopLevelAnalysisView,
  isTopLevelFileView,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import isAnalysisRunning from 'analysis-tool/isAnalysisRunning';
import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import useLogAnalysisProfilerTimingFinished from 'analysis-tool/ui/hooks/useLogAnalysisProfilerTimingFinished';
import {UseSelectedAnalysisItemOutput} from 'analysis-tool/ui/hooks/useSelectedAnalysisItem';
import {StepInitializationAnalysisMainViewItem} from 'analysis-tool/ui/main-view/MainViewTypes';
import ThirdPartyImportExportModal from 'analysis-tool/ui/main-view/third-party-step/ThirdPartyImportExportModal';
import isArchived from 'archive/isArchived';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FILTER_TABLE_FINISHED_EVENT,
  RUN_ANALYSIS_TEMPLATE_FINISHED_EVENT,
} from 'performance/processAnalysesEvents';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import AddIcon from 'ui-library/icons/AddIcon';
import runModal from 'ui-library/runModal';

import {DEFAULT_HORIZONTAL_PADDING} from './AnalysisUISidebarConstants';
import {
  getDatasetSectionSidebarItemDatas,
  getFilesSectionSidebarItemDatas,
  getParameterOptimizationConfigurationSidebarItemDatas,
  getLegacyParameterOptimizationSidebarItemDatasForAnalysis,
  getOutputSidebarItemDatas,
  getResultSidebarItemDatas,
  getAnalysisViewSidebarItemData,
} from './SidebarItemDatas';
import SidebarSection from './SidebarSection';
import ToggleAnalysisSidebarButton from './ToggleAnalysisSidebarButton';

export interface AnalysisUISidebarProps {
  analysis: AnalysisGraphQL;
  onCloseSidebar: () => void;
  // Analysis steps that require configuration are special because they open intermediate views in the ui. We
  // should not have one onOpen action for each step type
  onOpenStepInitializationView: (newSelectedItem: StepInitializationAnalysisMainViewItem) => void;
  onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  selectedItemState: UseSelectedAnalysisItemOutput;
}

/**
 * Wrapper component for handling shared archive-aware ui on sidebar `AddIcon` buttons.
 *
 * When analyses are archived, it renders a disabled `AddIcon` button with standard archived tooltip messaging;
 * otherwise, it renders the given button.
 */
function ArchivedButtonWrapper({
  analysis,
  children,
}: {
  analysis: AnalysisGraphQL;
  children: ReactNode;
}): JSX.Element | null {
  const archivedAddIcon = (
    <Tooltip title={ARCHIVED_ANALYSIS_TOOLTIP_TEXT}>
      <AddIcon disabled={true} />
    </Tooltip>
  );

  const archivedAndUnarchivable = isArchived(analysis) && analysis.permissions.unarchivable;

  return archivedAndUnarchivable ? archivedAddIcon : <>{children}</>;
}

function LegacyAnalysisUISidebarContent({
  analysis,
  onCloseSidebar,
  onOpenStepInitializationView,
  onChangeSelectedRightSidebarItem,
  selectedItemState,
}: AnalysisUISidebarProps): JSX.Element {
  const dataFrameCreationMethods = getEnabledDatasetCreationMethods();

  const hasParameterOptimizationDatasetStep = analysis.execution.steps.some(
    (step) => step.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION
  );

  const hasRunningStep = isAnalysisRunning(analysis);

  const canAddDataFrame =
    !hasRunningStep && !hasParameterOptimizationDatasetStep && dataFrameCreationMethods.length > 0;

  const topLevelDatasetSidebarItemDatas = getDatasetSectionSidebarItemDatas(analysis);
  const topLevelFileSidebarItemDatas = getFilesSectionSidebarItemDatas(analysis);
  const topLevelParameterOptimizationItemDatas =
    getLegacyParameterOptimizationSidebarItemDatasForAnalysis(analysis);
  const topLevelParameterOptimizationConfigurationItemDatas =
    getParameterOptimizationConfigurationSidebarItemDatas(analysis);
  const topLevelOutputSidebarItemDatas = getOutputSidebarItemDatas(analysis);
  const topLevelResultSidebarItemDatas = getResultSidebarItemDatas(analysis);

  function handleAddDataset(): void {
    onOpenStepInitializationView(getAddDataFrameAnalysisViewItem());
    if (dataFrameCreationMethods.length === 1) {
      trackSelectedDataFrameCreationMethodEvent(
        ActionOrigin.ANALYSIS_TOOL_ANALYSIS_PAGE_DATA_FRAME_SIDEPANEL,
        analysis.id,
        dataFrameCreationMethods.length,
        dataFrameCreationMethods[0]
      );
    }
  }

  useLogAnalysisProfilerTimingFinished(isAnalysisRunning(analysis), [
    RUN_ANALYSIS_TEMPLATE_FINISHED_EVENT,
    FILTER_TABLE_FINISHED_EVENT,
  ]);

  return (
    <div className={css(styles.sidebarContentWrapper)}>
      <div className={css(styles.sectionsWrapper)}>
        <SidebarSection
          analysis={analysis}
          headerButton={
            <ArchivedButtonWrapper analysis={analysis}>
              {analysis.permissions.writable ? (
                <Tooltip disabled={!canAddDataFrame} title="Add table">
                  <AddIcon
                    className={css(styles.addIcon)}
                    disabled={!canAddDataFrame}
                    onClick={handleAddDataset}
                  />
                </Tooltip>
              ) : null}
            </ArchivedButtonWrapper>
          }
          sidebarItemDatas={topLevelDatasetSidebarItemDatas}
          selectedItemState={selectedItemState}
          onOpenStepInitializationView={onOpenStepInitializationView}
          onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
          title="Analysis tables"
        />
        {topLevelParameterOptimizationItemDatas.length > 0 && (
          <div className={css(styles.secondarySidebarSectionWrapper)}>
            <SidebarSection
              analysis={analysis}
              sidebarItemDatas={topLevelParameterOptimizationItemDatas}
              selectedItemState={selectedItemState}
              title="Parameter Optimization"
              onOpenStepInitializationView={onOpenStepInitializationView}
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
            />
          </div>
        )}
        {topLevelParameterOptimizationConfigurationItemDatas.length > 0 && (
          <div className={css(styles.secondarySidebarSectionWrapper)}>
            <SidebarSection
              analysis={analysis}
              sidebarItemDatas={topLevelParameterOptimizationConfigurationItemDatas}
              selectedItemState={selectedItemState}
              title="Parameter Optimization"
              onOpenStepInitializationView={onOpenStepInitializationView}
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
            />
          </div>
        )}
        {topLevelFileSidebarItemDatas.length > 0 && (
          <div className={css(styles.secondarySidebarSectionWrapper)}>
            <SidebarSection
              analysis={analysis}
              sidebarItemDatas={topLevelFileSidebarItemDatas}
              selectedItemState={selectedItemState}
              title="Files"
              onOpenStepInitializationView={onOpenStepInitializationView}
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
            />
          </div>
        )}

        {topLevelResultSidebarItemDatas.length > 0 && (
          <div className={css(styles.secondarySidebarSectionWrapper)}>
            <SidebarSection
              analysis={analysis}
              onOpenStepInitializationView={onOpenStepInitializationView}
              sidebarItemDatas={topLevelResultSidebarItemDatas}
              selectedItemState={selectedItemState}
              title="Results"
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
            />
          </div>
        )}

        {topLevelOutputSidebarItemDatas.length > 0 && (
          <div className={css(styles.secondarySidebarSectionWrapper)}>
            <SidebarSection
              analysis={analysis}
              onOpenStepInitializationView={onOpenStepInitializationView}
              sidebarItemDatas={topLevelOutputSidebarItemDatas}
              selectedItemState={selectedItemState}
              title="Outputs"
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
            />
          </div>
        )}
      </div>
      <div className={css(styles.thirdPartyImportExportButtonWrapper)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          fullWidth={true}
          onClick={() => {
            runModal(ThirdPartyImportExportModal, {
              onRequestSubmit: () => {},
              onRequestClose: () => {},
              analysis,
            });
          }}
          size={ButtonSize.MEDIUM}
          text="Connect with external tool"
          dataTestAttrs={{component: 'AnalysisUISidebar', element: 'thirdPartyImportButton'}}
        />
      </div>
    </div>
  );
}

function AnalysisUISidebarContent({
  analysis,
  onCloseSidebar,
  onOpenStepInitializationView,
  onChangeSelectedRightSidebarItem,
  selectedItemState,
}: AnalysisUISidebarProps): JSX.Element {
  const dataFrameCreationMethods = getEnabledDatasetCreationMethods();

  // TODO(BNCH-121030): Revisit the need for the `hasParameterOptimizationDatasetStep` check
  const hasParameterOptimizationDatasetStep = analysis.execution.steps.some(
    (step) => step.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION
  );

  // TODO(BNCH-121030): Revisit the need for the `hasRunningStep` check
  const hasRunningStep = isAnalysisRunning(analysis);

  const nonLegacyViews = analysis.views.filter((view) => !isLegacyTopLevelAnalysisView(view));
  const topLevelTableViews = nonLegacyViews.filter(
    // We can't check isDataFrameView directly because it wouldn't account for views that are loading or
    // failed, so they don't have any outputs
    (view) => isTopLevelAnalysisView(view) && !isTopLevelFileView(view)
  );
  const topLevelFileViews = nonLegacyViews.filter((view) => isTopLevelFileView(view));

  // TODO(BNCH-125215): Remove these legacy item datas
  const legacyTopLevelResultSidebarItemDatas = getResultSidebarItemDatas(analysis);
  const legacyTopLevelOutputSidebarItemDatas = getOutputSidebarItemDatas(analysis);
  const legacyTopLevelParameterOptimizationItemDatas =
    getLegacyParameterOptimizationSidebarItemDatasForAnalysis(analysis);
  const legacyTopLevelParameterOptimizationConfigurationItemDatas =
    getParameterOptimizationConfigurationSidebarItemDatas(analysis);

  const canAddDataFrame =
    !hasRunningStep && !hasParameterOptimizationDatasetStep && dataFrameCreationMethods.length > 0;

  function handleAddDataset(): void {
    onOpenStepInitializationView(getAddDataFrameAnalysisViewItem());
    if (dataFrameCreationMethods.length === 1) {
      trackSelectedDataFrameCreationMethodEvent(
        ActionOrigin.ANALYSIS_TOOL_ANALYSIS_PAGE_DATA_FRAME_SIDEPANEL,
        analysis.id,
        dataFrameCreationMethods.length,
        dataFrameCreationMethods[0]
      );
    }
  }

  useLogAnalysisProfilerTimingFinished(isAnalysisRunning(analysis), [
    RUN_ANALYSIS_TEMPLATE_FINISHED_EVENT,
    FILTER_TABLE_FINISHED_EVENT,
  ]);

  return (
    <div className={css(styles.sidebarContentWrapper)}>
      <div className={css(styles.sectionsWrapper)}>
        <SidebarSection
          analysis={analysis}
          headerButton={
            <ArchivedButtonWrapper analysis={analysis}>
              {analysis.permissions.writable ? (
                <Tooltip disabled={!canAddDataFrame} title="Add table">
                  <AddIcon
                    className={css(styles.addIcon)}
                    disabled={!canAddDataFrame}
                    onClick={handleAddDataset}
                  />
                </Tooltip>
              ) : null}
            </ArchivedButtonWrapper>
          }
          sidebarItemDatas={topLevelTableViews.map((view) => getAnalysisViewSidebarItemData(view, analysis))}
          selectedItemState={selectedItemState}
          onOpenStepInitializationView={onOpenStepInitializationView}
          title="Analysis tables"
          onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
        />
        {topLevelFileViews.length > 0 && (
          <div className={css(styles.secondarySidebarSectionWrapper)}>
            <SidebarSection
              analysis={analysis}
              sidebarItemDatas={topLevelFileViews.map((view) =>
                getAnalysisViewSidebarItemData(view, analysis)
              )}
              selectedItemState={selectedItemState}
              title="Files"
              onOpenStepInitializationView={onOpenStepInitializationView}
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
            />
          </div>
        )}
        {legacyTopLevelParameterOptimizationItemDatas.length > 0 && (
          <div className={css(styles.secondarySidebarSectionWrapper)}>
            <SidebarSection
              analysis={analysis}
              sidebarItemDatas={legacyTopLevelParameterOptimizationItemDatas}
              selectedItemState={selectedItemState}
              title="Parameter Optimization"
              onOpenStepInitializationView={onOpenStepInitializationView}
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
            />
          </div>
        )}
        {legacyTopLevelParameterOptimizationConfigurationItemDatas.length > 0 && (
          <div className={css(styles.secondarySidebarSectionWrapper)}>
            <SidebarSection
              analysis={analysis}
              sidebarItemDatas={legacyTopLevelParameterOptimizationConfigurationItemDatas}
              selectedItemState={selectedItemState}
              title="Parameter Optimization"
              onOpenStepInitializationView={onOpenStepInitializationView}
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
            />
          </div>
        )}
        {legacyTopLevelResultSidebarItemDatas.length > 0 && (
          <div className={css(styles.secondarySidebarSectionWrapper)}>
            <SidebarSection
              analysis={analysis}
              onOpenStepInitializationView={onOpenStepInitializationView}
              sidebarItemDatas={legacyTopLevelResultSidebarItemDatas}
              selectedItemState={selectedItemState}
              title="Results"
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
            />
          </div>
        )}
        {legacyTopLevelOutputSidebarItemDatas.length > 0 && (
          <div className={css(styles.secondarySidebarSectionWrapper)}>
            <SidebarSection
              analysis={analysis}
              onOpenStepInitializationView={onOpenStepInitializationView}
              sidebarItemDatas={legacyTopLevelOutputSidebarItemDatas}
              selectedItemState={selectedItemState}
              title="Outputs"
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
            />
          </div>
        )}
      </div>
      <div className={css(styles.thirdPartyImportExportButtonWrapper)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          fullWidth={true}
          onClick={() => {
            runModal(ThirdPartyImportExportModal, {
              onRequestSubmit: () => {},
              onRequestClose: () => {},
              analysis,
            });
          }}
          size={ButtonSize.MEDIUM}
          text="Connect with external tool"
          dataTestAttrs={{component: 'AnalysisUISidebar', element: 'thirdPartyImportButton'}}
        />
      </div>
    </div>
  );
}

export default function AnalysisUISidebar(props: AnalysisUISidebarProps): JSX.Element {
  const {onCloseSidebar} = props;
  return (
    <>
      <div className={css(styles.collapseButtonWrapper)}>
        <ToggleAnalysisSidebarButton sidebarIsOpen={true} toggleSidebar={onCloseSidebar} />
      </div>
      {BenchlingConfig.ENABLE_ANALYSIS_TOOL_LAYOUTS_AND_VIEWS_REDESIGN ? (
        <AnalysisUISidebarContent {...props} />
      ) : (
        <LegacyAnalysisUISidebarContent {...props} />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  legacyContentWrapper: {
    paddingRight: DEFAULT_HORIZONTAL_PADDING,
  },
  sidebarContentWrapper: {
    paddingRight: DEFAULT_HORIZONTAL_PADDING,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  sectionsWrapper: {
    flexGrow: 1,
  },
  secondarySidebarSectionWrapper: {
    marginTop: 16,
  },
  collapseButtonWrapper: {
    paddingRight: 4,
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  thirdPartyImportExportButtonWrapper: {
    marginTop: 8,
  },
  addIcon: ADD_ICON_WITH_HOVER_STYLES,
});
